import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaFilter,
  FaTimes,
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaSyncAlt,
  FaSignOutAlt,
  FaUserCircle,
} from "react-icons/fa";

/**
 * Inventory FE with:
 * - AuthModal (login / signup) — bắt buộc trước khi search
 * - Username hiển thị + nút Đăng xuất góc phải header
 * - Debounced search, filters modal, sort, client pagination
 * - Mobile-first: nút to, spacing đủ, header sticky
 */

const BASE_API = "https://cam4k.vn/api";

/* ------------------ Auth Modal ------------------ */
function AuthModal({ open, onClose, onAuthed, defaultUsername }) {
  const [mode, setMode] = useState("login"); // 'login' | 'signup'
  const [u, setU] = useState(defaultUsername || "");
  const [p, setP] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (open) {
      setMsg("");
      setP("");
      if (!defaultUsername) setU("");
    }
  }, [open, defaultUsername]);

  async function login() {
    setMsg("");
    if (!u || !p) return setMsg("Nhập username và password.");
    setLoading(true);
    try {
      const res = await fetch(`${BASE_API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: u, password: p }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok)
        throw new Error(data.error || "Sai thông tin đăng nhập");
      localStorage.setItem("username", u);
      onAuthed(u);
      onClose();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function signup() {
    setMsg("");
    if (!u || !p) return setMsg("Nhập username và password.");
    setLoading(true);
    try {
      const res = await fetch(`${BASE_API}/accounts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: u,
          password: p,
          role: "viewer",
          status: "active",
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Tạo tài khoản thất bại");
      setMsg("Tạo tài khoản thành công. Đăng nhập để sử dụng.");
      setMode("login");
    } catch (e) {
      setMsg(e.message);
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/40">
      <div className="w-full md:w-[520px] max-h-[90vh] overflow-auto bg-white rounded-t-2xl md:rounded-2xl p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">
            {mode === "login" ? "Đăng nhập" : "Đăng ký"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100"
            aria-label="Đóng"
          >
            <FaTimes />
          </button>
        </div>

        <div className="grid gap-3">
          <input
            className="border rounded-xl px-3 py-3 text-base"
            placeholder="Username"
            value={u}
            onChange={(e) => setU(e.target.value)}
            autoFocus
          />
          <input
            className="border rounded-xl px-3 py-3 text-base"
            type="password"
            placeholder="Password"
            value={p}
            onChange={(e) => setP(e.target.value)}
          />

          {msg && (
            <div className="text-sm px-3 py-2 rounded-xl border bg-gray-50 text-gray-700">
              {msg}
            </div>
          )}

          {mode === "login" ? (
            <button
              onClick={login}
              disabled={loading}
              className="w-full py-3 rounded-xl bg-black text-white text-base active:opacity-80 disabled:opacity-60"
            >
              Đăng nhập
            </button>
          ) : (
            <button
              onClick={signup}
              disabled={loading}
              className="w-full py-3 rounded-xl bg-black text-white text-base active:opacity-80 disabled:opacity-60"
            >
              Đăng ký
            </button>
          )}

          <div className="text-sm text-center">
            {mode === "login" ? (
              <>
                Chưa có tài khoản?{" "}
                <button className="underline" onClick={() => setMode("signup")}>
                  Đăng ký
                </button>
              </>
            ) : (
              <>
                Đã có tài khoản?{" "}
                <button className="underline" onClick={() => setMode("login")}>
                  Đăng nhập
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------ SearchBar ------------------ */
function SearchBar({
  searchQuery,
  setSearchQuery,
  onSearch,
  onOpenFilters,
  isSearching,
  disabled,
}) {
  const inputRef = useRef(null);
  return (
    <div className="w-full flex items-center gap-3 mb-4">
      <div className="flex-1 flex items-center bg-white border rounded-xl px-3 py-2 shadow-sm">
        <FaSearch className="opacity-70 mr-2 shrink-0" aria-hidden />
        <input
          ref={inputRef}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !disabled && onSearch()}
          placeholder={
            disabled
              ? "Đăng nhập để tra cứu…"
              : "Nhập mã vật tư (Enter để tìm)…"
          }
          className="flex-1 outline-none text-sm md:text-base"
          disabled={disabled}
        />
        <button
          onClick={() => !disabled && onSearch()}
          className="ml-2 text-sm md:text-base px-3 py-2 rounded-lg bg-black text-white disabled:opacity-60"
          disabled={isSearching || disabled}
        >
          Tìm
        </button>
      </div>

      <button
        onClick={() => !disabled && onOpenFilters()}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white shadow-sm hover:bg-gray-50 disabled:opacity-60"
        aria-label="Mở bộ lọc"
        disabled={disabled}
      >
        <FaFilter /> Lọc
      </button>
    </div>
  );
}

/* ------------------ Filter Modal ------------------ */
function FilterModal({
  isOpen,
  onClose,
  khoList,
  loaiList,
  selectedKho,
  setSelectedKho,
  selectedLoai,
  setSelectedLoai,
  onApply,
}) {
  const [localKho, setLocalKho] = useState(selectedKho);
  const [localLoai, setLocalLoai] = useState(selectedLoai);
  const allKhoChecked = localKho.length === khoList.length;
  const allLoaiChecked = localLoai.length === loaiList.length;

  useEffect(() => {
    if (isOpen) {
      setLocalKho(selectedKho);
      setLocalLoai(selectedLoai);
    }
  }, [isOpen, selectedKho, selectedLoai]);

  const toggleAll = (type) => {
    if (type === "kho")
      setLocalKho(allKhoChecked ? [] : khoList.map((k) => k.value));
    else setLocalLoai(allLoaiChecked ? [] : loaiList.map((l) => l.value));
  };

  const toggleItem = (type, value) => {
    if (type === "kho") {
      setLocalKho((prev) =>
        prev.includes(value)
          ? prev.filter((v) => v !== value)
          : [...prev, value],
      );
    } else {
      setLocalLoai((prev) =>
        prev.includes(value)
          ? prev.filter((v) => v !== value)
          : [...prev, value],
      );
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40">
      <div className="w-full md:w-[min(900px,96vw)] max-h-[90vh] overflow-auto bg-white rounded-t-2xl md:rounded-2xl p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Bộ lọc</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100"
            aria-label="Đóng"
          >
            <FaTimes />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Kho */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium">Chọn Kho</h3>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={allKhoChecked}
                  onChange={() => toggleAll("kho")}
                />
                Chọn tất cả
              </label>
            </div>
            <div className="flex flex-wrap gap-3">
              {khoList.map((k) => (
                <label
                  key={k.value}
                  className="inline-flex items-center gap-2 text-sm border rounded-xl px-3 py-2 cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={localKho.includes(k.value)}
                    onChange={() => toggleItem("kho", k.value)}
                  />
                  {k.label}
                </label>
              ))}
            </div>
          </section>

          {/* Loại */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium">Chọn Loại</h3>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={allLoaiChecked}
                  onChange={() => toggleAll("loai")}
                />
                Chọn tất cả
              </label>
            </div>
            <div className="flex flex-wrap gap-3">
              {loaiList.map((l) => (
                <label
                  key={l.value}
                  className="inline-flex items-center gap-2 text-sm border rounded-xl px-3 py-2 cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={localLoai.includes(l.value)}
                    onChange={() => toggleItem("loai", l.value)}
                  />
                  {l.label}
                </label>
              ))}
            </div>
          </section>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border">
            Đóng
          </button>
          <button
            onClick={() => {
              setSelectedKho(localKho);
              setSelectedLoai(localLoai);
              onApply(localKho, localLoai);
              onClose();
            }}
            className="px-4 py-2 rounded-xl bg-black text-white"
          >
            Áp dụng
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------ FE Signature & Access helpers ------------------ */
async function sha256Hex(str) {
  const enc = new TextEncoder().encode(str);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
function toB64(bytes) {
  return btoa(String.fromCharCode(...bytes));
}
function fromB64(b64) {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}
async function computeDeviceFingerprint() {
  const nav = window.navigator || {};
  const scr = window.screen || {};
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  const data = [
    nav.userAgent,
    nav.language,
    nav.platform,
    nav.hardwareConcurrency,
    nav.deviceMemory,
    scr.width,
    scr.height,
    scr.colorDepth,
    tz,
  ].join("|");
  return await sha256Hex(data);
}
async function api(path, { method = "POST", body } = {}) {
  const res = await fetch(BASE_API + path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`${path} ${res.status}`);
  return res.json();
}
async function ensureDeviceRegistered(fp) {
  return api("/devices/register", {
    body: { device_fingerprint: fp, device_label: "Web FE" },
  });
}
async function requestAccess(username, fp) {
  return api("/access/request", { body: { username, device_fingerprint: fp } });
}
async function getAccessStatus(username, fp) {
  const url = `${BASE_API}/access/status?username=${encodeURIComponent(
    username,
  )}&device_fingerprint=${encodeURIComponent(fp)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`status ${res.status}`);
  return res.json();
}
async function issueSignature(username, fp, ttlDays = 90) {
  const random = new Uint8Array(32);
  crypto.getRandomValues(random);
  const signature_b64 = toB64(random);
  const payload = {
    device_fingerprint: fp,
    username,
    signature_type: "JWT",
    signature_b64,
    ttl_days: ttlDays,
    user_agent: navigator.userAgent,
  };
  const res = await api("/signatures/issue", { body: payload });
  return { signature_b64, expires_at: res.expires_at };
}
async function verifySignature(username, fp, signature_b64) {
  return api("/signatures/verify", {
    body: { device_fingerprint: fp, username, signature_b64 },
  });
}

/* ------------------ Main App ------------------ */
export default function App() {
  const [searchQuery, setSearchQuery] = useState("");
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [username, setUsername] = useState(
    () => localStorage.getItem("username") || "",
  );
  const [showAuth, setShowAuth] = useState(
    () => !localStorage.getItem("username"),
  );

  const [access, setAccess] = useState({ status: "unknown" });
  const [signature, setSignature] = useState(() => {
    const s = localStorage.getItem("fe_signature_b64");
    const exp = localStorage.getItem("fe_signature_exp");
    return s && exp ? { b64: s, exp: exp } : null;
  });
  const [deviceFp, setDeviceFp] = useState(
    localStorage.getItem("device_fp") || "",
  );

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filters
  const khoList = useMemo(
    () => [
      { label: "BC2", value: "BC2" },
      { label: "BC3", value: "BC3" },
      { label: "TG1", value: "TG1" },
      { label: "DT1", value: "DT1" },
      { label: "VL1", value: "VL1" },
      { label: "ST1", value: "ST1" },
      { label: "BL1", value: "BL1" },
      { label: "BD1", value: "BD1" },
      { label: "Nhà máy VTC", value: "VTC" },
    ],
    [],
  );
  const loaiList = useMemo(
    () => [
      { label: "Loại 001", value: "LOAI001" },
      { label: "Loại 002", value: "LOAI002" },
      { label: "Loại 003", value: "LOAI003" },
    ],
    [],
  );

  const [selectedKho, setSelectedKho] = useState(() => {
    const v = localStorage.getItem("filterKho");
    return v ? JSON.parse(v) : khoList.map((k) => k.value);
  });
  const [selectedLoai, setSelectedLoai] = useState(() => {
    const v = localStorage.getItem("filterLoai");
    return v ? JSON.parse(v) : loaiList.map((l) => l.value);
  });
  useEffect(() => {
    localStorage.setItem("filterKho", JSON.stringify(selectedKho));
  }, [selectedKho]);
  useEffect(() => {
    localStorage.setItem("filterLoai", JSON.stringify(selectedLoai));
  }, [selectedLoai]);

  // Sorting & pagination
  const [sortKey, setSortKey] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const sortedData = useMemo(() => {
    if (!sortKey) return tableData;
    const copy = [...tableData];
    copy.sort((a, b) => {
      const va = a?.[sortKey] ?? "";
      const vb = b?.[sortKey] ?? "";
      if (typeof va === "number" && typeof vb === "number")
        return sortAsc ? va - vb : vb - va;
      return sortAsc
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
    return copy;
  }, [tableData, sortKey, sortAsc]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const pagedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, page]);

  // Force auth before anything (first mount)
  useEffect(() => {
    if (!username) setShowAuth(true);
  }, [username]);

  // Setup: compute FP, register device, request/check access, ensure signature (sau khi có username)
  useEffect(() => {
    if (!username) return;
    (async () => {
      try {
        let fp = deviceFp;
        if (!fp) {
          fp = await computeDeviceFingerprint();
          setDeviceFp(fp);
          localStorage.setItem("device_fp", fp);
        }
        await ensureDeviceRegistered(fp);
        if (!localStorage.getItem("access_requested")) {
          await requestAccess(username, fp);
          localStorage.setItem("access_requested", "1");
        }
        const st = await getAccessStatus(username, fp);
        setAccess(st);

        let needNew = true;
        if (signature && signature.b64 && signature.exp) {
          const exp = Date.parse(signature.exp);
          needNew = isNaN(exp) || Date.now() > exp - 60 * 1000;
        }
        if (st.status === "approved") {
          if (needNew) {
            const { signature_b64, expires_at } = await issueSignature(
              username,
              fp,
            );
            setSignature({ b64: signature_b64, exp: expires_at });
            localStorage.setItem("fe_signature_b64", signature_b64);
            localStorage.setItem("fe_signature_exp", expires_at);
          }
        }
      } catch (e) {
        console.error("init error", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  // Debounced search (chỉ khi đã đăng nhập)
  useEffect(() => {
    if (!username || !searchQuery) return;
    const t = setTimeout(() => {
      handleSearch();
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedKho, selectedLoai, username]);

  const handleSearch = async () => {
    if (!username) {
      setShowAuth(true);
      return;
    }
    setPage(1);
    setError("");
    setIsLoading(true);
    try {
      if (access?.status !== "approved")
        throw new Error(
          "Thiết bị chưa được duyệt. Vui lòng đợi admin approve.",
        );
      const fp = deviceFp || (await computeDeviceFingerprint());
      const sig = signature?.b64 || localStorage.getItem("fe_signature_b64");
      if (!sig) throw new Error("Thiếu chữ ký FE");

      const body = {
        item_code: searchQuery,
        warehouse: selectedKho,
        type_item: selectedLoai,
      };
      const res = await fetch(`${BASE_API}/data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Device-Fingerprint": fp,
          "X-User": username,
          "X-Signature": sig,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error(`API error ${res.status}`);
      const data = await res.json();
      setTableData(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || "Có lỗi khi gọi API");
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const resetAll = () => {
    setSearchQuery("");
    setSelectedKho(khoList.map((k) => k.value));
    setSelectedLoai(loaiList.map((l) => l.value));
    setTableData([]);
    setPage(1);
    setSortKey(null);
  };

  const logout = () => {
    localStorage.removeItem("username");
    localStorage.removeItem("fe_signature_b64");
    localStorage.removeItem("fe_signature_exp");
    localStorage.removeItem("device_fp");
    localStorage.removeItem("access_requested");
    setUsername("");
    setAccess({ status: "unknown" });
    setSignature(null);
    setShowAuth(true);
    setTableData([]);
  };

  const headerCell = (label, key) => {
    const isActive = sortKey === key;
    return (
      <th className="md:px-3 py-3 text-left whitespace-nowrap">
        <button
          className={`inline-flex items-center gap-1 select-none ${
            isActive ? "font-semibold" : "font-normal"
          }`}
          onClick={() => {
            if (!sortKey || sortKey !== key) {
              setSortKey(key);
              setSortAsc(true);
            } else {
              setSortAsc(!sortAsc);
            }
          }}
        >
          {label}
          {isActive ? (
            sortAsc ? (
              <FaSortUp />
            ) : (
              <FaSortDown />
            )
          ) : (
            <FaSort className="opacity-40" />
          )}
        </button>
      </th>
    );
  };

  const disabledSearch = !username;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <header className="sticky top-0 z-40 bg-white ">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <h1 className="text-lg font-semibold">TÔ THÀNH PHÁT</h1>

          {/* Username + Logout (góc phải) */}
          <div className="ml-auto flex items-center gap-2">
            {username ? (
              <>
                <div className="hidden sm:flex items-center gap-2 text-sm px-3 py-1.5 rounded-full bg-gray-100">
                  <FaUserCircle className="opacity-70" />{" "}
                  <span className="font-medium">{username}</span>
                </div>
                <button
                  onClick={logout}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-gray-50"
                  title="Đăng xuất"
                >
                  <FaSignOutAlt />{" "}
                  <span className="hidden sm:inline">Thoát</span>
                </button>
              </>
            ) : (
              <button
                onClick={() => setShowAuth(true)}
                className="px-3 py-2 rounded-xl bg-black text-white"
              >
                Đăng nhập
              </button>
            )}

            <button
              onClick={() => setIsModalOpen(true)}
              className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-gray-50"
            >
              <FaFilter /> Bộ lọc
            </button>

            <button
              onClick={resetAll}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-gray-50"
              title="Làm mới"
            >
              <FaSyncAlt />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto md:px-4 py-4">
        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onSearch={handleSearch}
          onOpenFilters={() => setIsModalOpen(true)}
          isSearching={isLoading}
          disabled={disabledSearch}
        />
        {/* Trạng thái */}
        {!username && (
          <div className="mb-3 rounded-xl border border-yellow-200 bg-yellow-50 text-yellow-800 px-4 py-3">
            Vui lòng đăng nhập/đăng ký để sử dụng chức năng tra cứu.
          </div>
        )}
        {error && (
          <div className="mb-3 rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3">
            Lỗi: {error}
          </div>
        )}
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border border-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  {headerCell("Mã vật tư", "item_code")}
                  {headerCell("QC", "model")}
                  {headerCell("Size", "lot")}
                  {headerCell("Khả dụng", "available_stock")}
                  {headerCell("Tồn kho", "inventory_stock")}
                  {headerCell("Kho", "warehouse")}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center">
                      <div className="inline-flex items-center gap-3">
                        <svg
                          className="animate-spin h-5 w-5"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                            opacity="0.25"
                          />
                          <path
                            d="M22 12a10 10 0 0 1-10 10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                        </svg>
                        Đang tải dữ liệu…
                      </div>
                    </td>
                  </tr>
                ) : sortedData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-10 text-center text-gray-500"
                    >
                      {searchQuery
                        ? "Không có kết quả phù hợp."
                        : "Nhập mã vật tư để tra cứu."}
                    </td>
                  </tr>
                ) : (
                  pagedData.map((row) => (
                    <tr
                      key={row.id}
                      className=" last:border-0 hover:bg-gray-50/60"
                    >
                      <td className="md:px-3 py-2 whitespace-nowrap border-r border-b border-gray-200">
                        {row.item_code ?? ""}
                      </td>
                      <td className="md:px-3 py-2 text-center whitespace-nowrap border-r border-b border-gray-200">
                        {row.model ?? ""}
                      </td>
                      <td className="md:px-3 py-2 text-center whitespace-nowrap border-r border-b border-gray-200">
                        {row.lot ?? ""}
                      </td>
                      <td className="md:px-3 py-2 text-center tabular-nums border-r border-b border-gray-200">
                        {row.available_stock ?? ""}
                      </td>
                      <td className="md:px-3 py-2 text-center tabular-nums border-r border-b border-gray-200">
                        {row.inventory_stock ?? ""}
                      </td>
                      <td className="md:px-3 py-2 text-center border-b whitespace-nowrap">
                        {row.warehouse ?? ""}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Phân trang */}
          <div className="flex items-center justify-between px-4 py-3">
            <div className="text-xs text-gray-500">
              {sortedData.length > 0 && (
                <>
                  Hiển thị {(page - 1) * pageSize + 1}-
                  {Math.min(page * pageSize, sortedData.length)} /{" "}
                  {sortedData.length} bản ghi
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1.5 rounded-lg border disabled:opacity-50"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Trước
              </button>
              <span className="text-sm">
                Trang {page}/{totalPages}
              </span>
              <button
                className="px-3 py-1.5 rounded-lg border disabled:opacity-50"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Sau
              </button>
            </div>
          </div>
        </div>
        {/* Nút Bộ lọc to cho mobile */}
        <div className="mt-4 md:hidden">
          <button
            onClick={() => setIsModalOpen(true)}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border bg-white hover:bg-gray-50"
          >
            <FaFilter /> Bộ lọc
          </button>
        </div>
      </main>

      <footer className="max-w-7xl mx-auto px-4 py-6 text-xs text-gray-500">
        © {new Date().getFullYear()} Cam4K Software.
      </footer>

      {/* Modals */}
      <FilterModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        khoList={khoList}
        loaiList={loaiList}
        selectedKho={selectedKho}
        setSelectedKho={setSelectedKho}
        selectedLoai={selectedLoai}
        setSelectedLoai={setSelectedLoai}
        onApply={() => {
          if (searchQuery) handleSearch();
        }}
      />
      <AuthModal
        open={showAuth}
        onClose={() => setShowAuth(false)}
        onAuthed={(u) => setUsername(u)}
        defaultUsername={username}
      />
    </div>
  );
}
